import { NextUIProvider } from '@nextui-org/react'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import type { FC } from 'react'
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { Toaster } from 'react-hot-toast'
import { Provider, useSelector } from 'react-redux'
import {
  BrowserRouter,
  MemoryRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom'
import { AccountLockedView } from '~/components/auth/AccountLockedView'
import { WorkspaceSettingsXmlLock } from '~/components/auth/WorkspaceSettingsXmlLock'
import { Container, Orientation } from '~/components/common/Container'
import Errors from '~/components/common/Errors'
import { FlashMessageHandler } from '~/components/common/FlashMessageHandler'
import { FlashMessageUrlHandler } from '~/components/common/FlashMessageUrlHandler'
import { LoadingState } from '~/components/common/LoadingSpinner'
import { NoWorkspaceView } from '~/components/auth/NoWorkspaceView'
import { TaskDisplay } from '~/components/task/TaskDisplay'
import TopAlertBanner from '~/components/common/TopAlertBanner'
import Typography from '~/components/common/Typography'
import {
  loadExampleProjectAsFile,
  loadExampleProjectAsS3Path
} from '~/components/campaign/MuleProjectUpload'
import { ImageViewerModal } from '~/components/modals/ImageViewerModal'
import { TaskStatusDetailModal } from '~/components/modals/TaskStatusDetailsModal'
import SettingsPage from '~/components/settings/SettingsPage'
import { RefactorFileViewerSlide } from '~/components/mule-refactor/slides/RefactorFileViewerSlide'
import Sidebar from '~/components/sidebar/Sidebar'
import {
  AgentCommand,
  FixedPromptCommands,
  UsedCommandType,
  isChatCommand
} from '~/constants/commands'
import { AccountType } from '~/constants/settings'
import ChatDisplayController from '~/containers/ChatDisplayController'
import { AppConfigProvider, useAgentUnavailableToast } from '~/contexts/AppConfigContext'
import { BillingProvider, useBillingContext } from '~/contexts/BillingContext'
import { ConversationProvider, useConversationsContext } from '~/contexts/ConversationContext'
import {
  CurrentConversationProvider,
  useCurrentConversation
} from '~/contexts/CurrentConversationContext'
import { GlobalStreamingProvider } from '~/contexts/GlobalStreamingContext'
import { PlatformProvider } from '~/contexts/PlatformContext'
import { SidebarProvider, useSidebarContext } from '~/contexts/SidebarContext'
import { TaskProvider } from '~/contexts/TaskContext'
import { UserProvider, useUserContext } from '~/contexts/UserContext'
import { useUser, useVerificationRedirect } from '~/hooks'
import { CommonModal, ModalId } from '~/hooks/useModal'
import { usePlanSelection } from '~/hooks/usePlanSelection'
import { useTracer } from '~/hooks/useTracer'
import {
  AgentRequest,
  CodeContext,
  CommandType,
  Metadata,
  commandTypeFromJSON
} from '~/protos/agents'
import { bridge, initBridge } from '~/services/bridge'
import { UserSocket } from '~/services/socket'
import { performLogout } from '~/services/bridge/pluginAuth'
import { localStoragePersister as persister, queryClient } from '~/services/queryClient'
import { getSuperAdminHeartbeat } from '~/services/superAdminService'
import store, { RootState } from '~/store'
import { initializeUserAndHandles } from '~/store/dirHandleSlice'
import { hideExamplePopover, hideNonFormPopover } from '~/store/galleryScreenSlice'
import { RepositoryBranch } from '~/types/Conversations'
import { createAndRedirectToCheckout } from '~/utils/checkoutUtils'
import { cronScheduler, setCronSchedulerStore } from '~/utils/cronScheduler'
import { createTask } from '~/utils/eventUtils'
import { parseRepositoryName } from '~/utils/repositoryUtils'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'
import { cm, isPlatformDomain, isSuperAdminSubdomain } from '~/utils/utils'
import DwlSampleDataGeneratorPageComponent from './dwl-sample-data-generator'
import FlowDocGeneratorPageComponent from './flow-doc-generator'
import HumanAnnotationsPageComponent from './human-annotations'
import MasterLicenseAgreementPageComponent from './master-license-agreement'
import MtToDwlPageComponent from './mt-to-dwl'
import OnboardPageComponent from './onboard'
import { default as PluginAuthSuccessPageComponent } from './plugin-auth-success'
import PrivacyPageComponent from './privacy'
import ResetPasswordPageComponent from './reset-password'
import SetupMfaPageComponent from './setup-mfa'
import SignInPageComponent from './signin'
import SignUpPageComponent from './signup'
import SsoSetupPageComponent from './sso-setup'
import { default as SuperAdminPageComponent } from './super-admin'
import TermsPageComponent from './terms'
import TestDrivePageComponent from './test-drive'
import VerifyEmailPageComponent from './verify-email'

import { ImpersonationBanner } from '~/components/super-admin/ImpersonationBanner'
import PhoneVerificationPage from '~/components/auth/PhoneVerificationPage'
import { WorkspaceHomePage } from '~/components/layout/WorkspaceHomePage'
import { TestAgentResultScreen } from '~/components/integration-test/TestAgentResultScreen'
import { PageView } from '~/components/settings/components/types'
import { BillingAccountsProvider } from '~/contexts/BillingAccountsContext'
import { ImpersonationProvider, useImpersonation } from '~/contexts/ImpersonationContext'
import { useIndexesInvalidator } from '~/contexts/IndexesContext'
import { ModalProvider, useModalContext } from '~/contexts/ModalContext'
import { WorkspaceProvider, useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { PluginWorkspaceProvider, usePluginWorkspace } from '~/contexts/PluginWorkspaceContext'
import { PluginVersionGate } from '~/components/plugin/PluginVersionGate'
import AcceptBillingInvitePage from './accept-billing-invite'
import CheckoutCallbackPage from './checkout-callback'
import JoinAccountPage from './join-account'

// Initialize bridge (no-op in browser, activates in IDE plugin)
initBridge()

// Initialize the cron scheduler with the Redux store
setCronSchedulerStore(store)

if (process.env.NODE_ENV !== 'development') {
  console.info('rev:', __GIT_REV__)
}

/**
 * Redirect component for /super-admin routes on platform.curietech.ai
 * Super admin should only be accessible via superadmin.curietech.ai
 */
function PlatformSuperAdminRedirect() {
  useEffect(() => {
    const currentPath = window.location.pathname
    window.location.href = `https://superadmin.curietech.ai${currentPath}`
  }, [])

  return <LoadingSpinner />
}

// Route protection components
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loaded } = useUserContext()
  const { needsRedirect, redirectUrl } = useVerificationRedirect()
  const navigate = useNavigate()

  // Redirect to signin if not authenticated
  useEffect(() => {
    if (loaded && !isAuthenticated) {
      // On super admin subdomain, redirect to signin page instead of root
      // to avoid a redirect loop (since root is also protected on super admin)
      if (isSuperAdminSubdomain()) {
        navigate('/signin', { replace: true })
      } else {
        navigate('/', { replace: true })
      }
    }
  }, [loaded, isAuthenticated, navigate])

  // Redirect to verification page if needed
  useEffect(() => {
    if (loaded && isAuthenticated && needsRedirect && redirectUrl) {
      navigate(redirectUrl, { replace: true })
    }
  }, [loaded, isAuthenticated, needsRedirect, redirectUrl, navigate])

  // Wait for authentication state to be determined
  if (!loaded) {
    return <LoadingSpinner />
  }

  if (!isAuthenticated) {
    return null
  }

  // Block rendering if verification is required (prevents flash of content before redirect)
  if (needsRedirect) {
    return <LoadingSpinner />
  }

  return <WorkspaceSettingsXmlGate>{children}</WorkspaceSettingsXmlGate>
}

/**
 * Mandatory own-settings.xml gate. It hangs off `ProtectedRoute`, the guard
 * every authenticated route goes through, so a URL typed straight into the
 * address bar is locked out too — the requested page never mounts or fetches.
 *
 * This is a child component rather than inline in `ProtectedRoute` because it
 * reads `useWorkspaceContext`, and `WorkspaceProvider` is only mounted for
 * authenticated users (see `AuthenticatedAppShell`). Inline, that hook ran on
 * every render of the guard — including unauthenticated ones — and threw
 * before the guard could return null or redirect to signin. Hooks cannot be
 * conditional, so the super-admin exemption below could never protect it. The
 * result was a blank page for any logged-out visitor to a protected route, and
 * for *every* visitor to superadmin.curietech.ai, whose root route is itself
 * protected, taking out the login redirect with it. Rendered only past the
 * guard's authenticated checks, the hook is always inside its provider.
 */
function WorkspaceSettingsXmlGate({ children }: { children: React.ReactNode }) {
  const { currentWorkspace, isLoading: workspaceLoading } = useWorkspaceContext()
  const { pathname } = useLocation()

  // Super-admin surfaces are exempt: a Curie admin's tooling shouldn't be held
  // hostage by a customer workspace's runtime settings.
  //
  // Settings is deliberately exempt. It runs no Mule build — the guard that
  // actually stops one resolving Curie's bundled EE credentials lives in the
  // agent runtime (CUR-1686), not here — and its Workspace tab is the other
  // place an admin uploads settings.xml (`XmlSettingsSection`). Locking it
  // would take away a legitimate way to comply.
  const isSettingsPath = pathname === '/settings' || pathname.endsWith('/settings')

  if (isSuperAdminSubdomain() || isSettingsPath) {
    return <>{children}</>
  }

  // Hold the page back until we know whether this workspace is gated.
  // Rendering optimistically flashes the page AND fires its API calls before
  // the lock lands — the exact leak this gate exists to prevent.
  if (workspaceLoading) {
    return <LoadingSpinner />
  }

  if (currentWorkspace?.settings_xml_required === true) {
    return (
      <LockedShell>
        <WorkspaceSettingsXmlLock />
      </LockedShell>
    )
  }

  return <>{children}</>
}

/**
 * Account Lock Guard - blocks access when billing account is locked
 * Shows AccountLockedView in the content area while keeping sidebar accessible
 * This allows users to sign out or switch accounts even when locked
 */
function AccountLockGuard({ children }: { children: React.ReactNode }) {
  const { isAccountLocked, lockStatus, lockStatusLoading } = useBillingContext()

  // Show loading while checking lock status (with sidebar)
  if (lockStatusLoading) {
    return (
      <LockedShell>
        <div className='flex items-center justify-center h-full w-full'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4 mx-auto'></div>
            <p className='text-tokens-neutral-text-secondary'>Checking account status...</p>
          </div>
        </div>
      </LockedShell>
    )
  }

  // Show locked view in content area if account is locked (sidebar still accessible)
  if (isAccountLocked && lockStatus) {
    return (
      <LockedShell>
        <AccountLockedView
          creditDebt={lockStatus.credit_debt}
          minimumCreditsToUnlock={lockStatus.minimum_credits_to_unlock}
          lockReason={lockStatus.lock_reason}
        />
      </LockedShell>
    )
  }

  return <>{children}</>
}

/**
 * Renders a blocking state in the content area with the sidebar intact, so a
 * blocked user can still switch workspace or account, or log out. Shared by the
 * billing lock (`AccountLockGuard`) and the own-settings.xml lock.
 */
function LockedShell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()

  return (
    <ConversationProvider>
      <SidebarProvider>
        <div className='h-screen w-screen flex flex-col'>
          <div className='flex grow bg-tokens-neutral-bg-grey-1 text-tokens-neutral-text-primary min-h-0'>
            <Container
              id='sidebar|main'
              orientation={Orientation.HORIZONTAL}
              className='w-screen bg-tokens-neutral-bg-grey-1'
              initialSize={271}
              isResizable={false}
              firstChild={<Sidebar handleRoot={() => navigate('/')} />}
              firstClassName='overflow-hidden bg-tokens-neutral-bg-white border-r border-tokens-neutral-border-2'
              secondClassName='overflow-auto'
              secondChild={children}
            />
          </div>
        </div>
      </SidebarProvider>
    </ConversationProvider>
  )
}

export function UnauthenticatedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loaded } = useUserContext()
  const navigate = useNavigate()

  useEffect(() => {
    if (loaded && isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [loaded, isAuthenticated, navigate])

  // Wait for authentication state to be determined
  if (!loaded) {
    return <LoadingSpinner />
  }

  if (isAuthenticated) {
    return null
  }

  return <>{children}</>
}

/**
 * Guard component for super admin access.
 * Checks if user has super admin permission and handles access denied scenarios.
 */
export function SuperAdminAccessGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const [accessState, setAccessState] = useState<'loading' | 'granted' | 'denied'>('loading')
  const isSuperAdminHost = isSuperAdminSubdomain()
  const { isImpersonating, stopImpersonation, loading: impersonationLoading } = useImpersonation()

  useEffect(() => {
    if (impersonationLoading) return
    if (isImpersonating) {
      stopImpersonation()
      return
    }

    const checkAccess = async () => {
      try {
        await getSuperAdminHeartbeat()
        setAccessState('granted')
      } catch {
        setAccessState('denied')
        if (!isSuperAdminHost) {
          navigate('/', { replace: true })
        }
      }
    }
    checkAccess()
  }, [navigate, isSuperAdminHost, isImpersonating, impersonationLoading, stopImpersonation])

  if (accessState === 'loading') {
    return <LoadingSpinner />
  }

  if (accessState === 'denied') {
    // On super admin subdomain, show access denied with logout option
    if (isSuperAdminHost) {
      return (
        <div className='flex items-center justify-center h-screen bg-gray-50'>
          <div className='text-center max-w-md p-8 bg-white rounded-lg shadow-md'>
            <div className='mb-6'>
              <svg
                className='w-16 h-16 mx-auto text-red-500'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                />
              </svg>
            </div>
            <h1 className='text-2xl font-bold mb-4 text-gray-900'>Access Denied</h1>
            <p className='text-gray-600 mb-6'>
              You do not have permission to access the Super Admin panel. Please contact an
              administrator if you believe this is an error.
            </p>
            <button
              onClick={() => {
                performLogout()
              }}
              className='px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors'
            >
              Sign Out
            </button>
          </div>
        </div>
      )
    }
    return null
  }

  return <>{children}</>
}

/**
 * Router component for super admin subdomain that handles impersonation.
 * When impersonating, shows the workspace view instead of super admin panel.
 * When not impersonating, shows the super admin panel.
 */
export function SuperAdminSubdomainRootRouter() {
  const { isImpersonating, loading: impersonationLoading } = useImpersonation()

  if (impersonationLoading) {
    return <LoadingSpinner />
  }

  // When impersonating, show the normal app workspace view. The workspace and
  // modal providers come from `AuthenticatedAppShell` further up the tree.
  if (isImpersonating) {
    return <AuthenticatedRootController />
  }

  // Not impersonating, show the super admin panel
  return (
    <SuperAdminAccessGuard>
      <SuperAdminPageComponent />
    </SuperAdminAccessGuard>
  )
}

/**
 * Guard for super admin tabs (/:tab/*) that redirects to root when impersonating.
 * This ensures that when impersonating, navigating to super admin tabs like /dashboard
 * redirects to the workspace instead.
 */
export function SuperAdminTabGuard({ children }: { children: React.ReactNode }) {
  const { isImpersonating, loading: impersonationLoading } = useImpersonation()
  const navigate = useNavigate()

  useEffect(() => {
    if (!impersonationLoading && isImpersonating) {
      // When impersonating, redirect to root which will show the workspace
      navigate('/', { replace: true })
    }
  }, [isImpersonating, impersonationLoading, navigate])

  if (impersonationLoading) {
    return <LoadingSpinner />
  }

  // If impersonating, don't render children (redirect will happen)
  if (isImpersonating) {
    return <LoadingSpinner />
  }

  return <>{children}</>
}

interface FixedPromptTaskProps {
  command: AgentCommand
  withExamplePrefilled?: boolean
  repositories?: RepositoryBranch[]
  s3UploadPaths?: string[]
}

export function LoadingSpinner() {
  return (
    <div className='flex items-center justify-center h-screen'>
      <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500'></div>
    </div>
  )
}

function SimplePage({ title, content }: { title: string; content: string }) {
  return (
    <div className='flex items-center justify-center h-screen'>
      <div className='text-center max-w-2xl p-8'>
        <h1 className='text-2xl font-bold mb-4'>{title}</h1>
        <p className='text-gray-600'>{content}</p>
      </div>
    </div>
  )
}

export function CurrentConversationContextProvider({ children }: { children: React.ReactNode }) {
  return (
    <CurrentConversationProvider>
      <GlobalStreamingProvider>
        <UtilsProvider>{children}</UtilsProvider>
      </GlobalStreamingProvider>
    </CurrentConversationProvider>
  )
}

export function UnauthenticatedRootController() {
  const navigate = useNavigate()

  useEffect(() => {
    navigate('/signin', { replace: true })
  }, [navigate])

  return <LoadingSpinner />
}

export function WorkspaceValidationComponent({ children }: { children: React.ReactNode }) {
  const { workspaceUuid } = useParams<{ workspaceUuid: string }>()
  const { workspaces, isLoading: workspaceLoading } = useWorkspaceContext()
  const { currentWorkspace } = useWorkspaceContext()
  const { hasLicense, allLicenses, loaded: billingLoaded } = useBillingContext()
  const {
    openOnboarding,
    openZeroCredits,
    zeroCreditsOpen,
    planOpen,
    wasOnboardingShownFor,
    markOnboardingShownFor
  } = useModalContext()
  // Onboarding "already shown" tracking is per-workspace and lives on
  // ModalContext via `wasOnboardingShownFor` / `markOnboardingShownFor`,
  // so it survives this component's remounts (e.g. user navigates to `/`
  // and comes back) and is naturally scoped per workspace.
  const [openedZeroCreditsModal, setOpenedZeroCreditsModal] = useState<boolean>(false)
  // Tracks whether the zero-credits check (account-level) has completed,
  // so workspace-level onboarding can safely proceed after it.
  const [zeroCreditsChecked, setZeroCreditsChecked] = useState<boolean>(false)
  const location = useLocation()

  // ── Step 1: Zero-credits check (account-level — runs first) ──
  useEffect(() => {
    if (!billingLoaded || !allLicenses || zeroCreditsChecked) return
    if (!currentWorkspace) return

    const isWorkspacePage = /^\/w\/[^/]+/.test(location.pathname)
    const isSettingsPage = location.pathname === '/settings'
    if (!isWorkspacePage && !isSettingsPage) return

    const isFlexOnly =
      hasLicense(AccountType.FLEX) &&
      !hasLicense(AccountType.PRO) &&
      !hasLicense(AccountType.PRO_PLUS) &&
      !hasLicense(AccountType.TEAM) &&
      !hasLicense(AccountType.ENTERPRISE) &&
      !hasLicense(AccountType.ENTERPRISE_MIGRATE) &&
      !hasLicense(AccountType.ENTERPRISE_MIGRATE_ASSESS) &&
      !hasLicense(AccountType.ENTERPRISE_MIGRATE_ACCELERATE) &&
      !hasLicense(AccountType.ENTERPRISE_MIGRATE_REFACTOR)
    const hasZeroCredits = (allLicenses.credits_remaining ?? 0) <= 0

    if (isFlexOnly && hasZeroCredits) {
      openZeroCredits()
      setOpenedZeroCreditsModal(true)
      // zeroCreditsChecked stays false — will be set when dialog closes
    } else {
      // Not applicable → mark as checked so onboarding can proceed
      setZeroCreditsChecked(true)
    }
  }, [
    billingLoaded,
    allLicenses,
    currentWorkspace,
    hasLicense,
    openZeroCredits,
    location.pathname,
    zeroCreditsChecked
  ])

  // When zero-credits dialog closes, mark step as done so onboarding can proceed
  useEffect(() => {
    if (openedZeroCreditsModal && !zeroCreditsOpen) {
      setZeroCreditsChecked(true)
    }
  }, [openedZeroCreditsModal, zeroCreditsOpen])

  // ── Step 2: Onboarding (workspace-level — runs after zero-credits step completes) ──
  // Also waits for PlanDialog to close (user may have clicked "Upgrade Plan" from ZeroCreditsDialog).
  useEffect(() => {
    if (!zeroCreditsChecked) return // Wait for account-level check to finish
    if (planOpen) return // Wait for PlanDialog to close before showing onboarding
    if (!currentWorkspace) return
    // Skip auto-open if we've already shown it for this workspace this
    // session — covers both the "already opened on this mount" and the
    // "user dismissed via Do it later before remount" cases in one ref.
    if (wasOnboardingShownFor(currentWorkspace.uuid)) return

    const isWorkspacePage = /^\/w\/[^/]+/.test(location.pathname)
    const isSettingsPage = location.pathname === '/settings'
    if (!isWorkspacePage && !isSettingsPage) return

    // The mandatory own-settings.xml gate is NOT handled here — a gated
    // workspace is locked in `ProtectedRoute`. Settings is the one page it
    // still lets through, and stacking the welcome modal on top of a locked
    // workspace would just cover the upload the user came here for.
    if (currentWorkspace.settings_xml_required === true) return

    if (!currentWorkspace.onboarded && currentWorkspace.is_admin) {
      openOnboarding()
      markOnboardingShownFor(currentWorkspace.uuid)
    }
  }, [
    zeroCreditsChecked,
    planOpen,
    currentWorkspace,
    openOnboarding,
    location.pathname,
    wasOnboardingShownFor,
    markOnboardingShownFor
  ])

  // Reset zero-credits state on workspace change so the account-level
  // check re-runs. Onboarding tracking is per-workspace-uuid in
  // ModalContext, so it doesn't need a reset here.
  useEffect(() => {
    setOpenedZeroCreditsModal(false)
    setZeroCreditsChecked(false)
  }, [workspaceUuid])

  // Show loading while validating
  if (workspaceLoading) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4 mx-auto'></div>
          <p className='text-gray-600 select-none'>Loading workspace...</p>
        </div>
      </div>
    )
  }

  // Validate workspace UUID from URL
  if (workspaceUuid) {
    const isValid = workspaces.some(w => w.uuid === workspaceUuid)

    if (!isValid) {
      // Invalid workspace UUID - show error instead of redirecting
      return (
        <div className='flex items-center justify-center h-screen'>
          <div className='text-center max-w-md p-8'>
            <h1 className='text-2xl font-bold mb-4'>Workspace Not Found</h1>
            <p className='text-gray-600 mb-4'>
              You don&apos;t have access to this workspace or it doesn&apos;t exist.
            </p>
            <p className='text-sm text-gray-500 mb-6'>Workspace ID: {workspaceUuid}</p>
            <a
              href='/'
              className='inline-block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
            >
              Go to Home
            </a>
          </div>
        </div>
      )
    }
  }
  return <>{children}</>
}

export function WorkspaceLevelProvider({ children }: { children: React.ReactNode }) {
  return (
    <WorkspaceProvider>
      <ModalProvider>
        <UserSetupHandler>{children}</UserSetupHandler>
      </ModalProvider>
    </WorkspaceProvider>
  )
}

/**
 * Hoists the workspace-level providers above `<Routes>` so the entire provider
 * tree (workspace fetch, conversations list, modals, indexes, listeners) does
 * not unmount/remount when the user navigates between top-level paths — e.g.
 * clicking the sidebar logo at `/w/:uuid/c/:conv` round-trips through `/` and
 * back to `/w/:uuid` without tearing the tree down.
 *
 * For unauthenticated users (login/signup pages, etc.) the providers are
 * skipped entirely.
 */
function AuthenticatedAppShell({
  isAuthenticated,
  children
}: {
  isAuthenticated: boolean
  children: React.ReactNode
}) {
  if (!isAuthenticated) return <>{children}</>
  return (
    <WorkspaceLevelProvider>
      <ConversationProvider>
        <AuthenticatedAppEffects />
        {children}
      </ConversationProvider>
    </WorkspaceLevelProvider>
  )
}

// Mounts global side-effect hooks that should run for the lifetime of the
// authenticated session — currently the React Query invalidator for indexes.
// Lives as its own component so its effects don't pollute `AuthenticatedAppShell`.
function AuthenticatedAppEffects() {
  useIndexesInvalidator()
  return null
}

export function AuthenticatedRootController() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useUserContext()
  const {
    hasLicense,
    loaded: billingLoaded,
    billingInfo,
    isAccountLocked,
    lockStatus,
    lockStatusLoading
  } = useBillingContext()
  const { currentWorkspace, isLoading: workspaceLoading } = useWorkspaceContext()
  const [settingsProcessed, setSettingsProcessed] = useState(false)
  const [verificationProcessed, setVerificationProcessed] = useState(false)
  const { selectedPlan, clearPlan } = usePlanSelection()

  // Use the verification hook for redirect logic
  const { needsRedirect, redirectUrl } = useVerificationRedirect()
  const isFreeLicense = hasLicense(AccountType.FLEX)

  // Authentication verification flow - runs first
  useEffect(() => {
    const runVerificationFlow = async () => {
      if (!user) {
        return
      }

      try {
        // Check if any verification is required
        if (needsRedirect && redirectUrl) {
          navigate(redirectUrl, { replace: true })
          return
        }

        // Wait for billing to load before proceeding with billing-dependent checks
        if (!billingLoaded) {
          return
        }

        // Handle plan-based payment redirect after signup
        // If user signed up with a specific plan (not 'flex'), redirect to payment
        if (selectedPlan && selectedPlan !== 'flex' && isFreeLicense) {
          try {
            if (!billingInfo?.id) {
              console.error('Cannot create checkout session: billing account not found')
              clearPlan()
              return
            }
            await createAndRedirectToCheckout({
              billingId: billingInfo.id,
              accountType: selectedPlan,
              teamSize: selectedPlan === 'team' ? 5 : 1,
              cancelUrl: `${window.location.origin}/`,
              onSuccess: () => clearPlan()
            })
            // If we reach here, redirect happened, function will return above
            return
          } catch (error) {
            console.error('Failed to create checkout session:', error)
            // Continue with normal flow on error and clear plan
            clearPlan()
          }
        }

        // All verifications passed
        setVerificationProcessed(true)
      } catch (error) {
        console.error('Authentication verification error:', error)
        // On error, allow access but log the issue
        setVerificationProcessed(true)
      }
    }

    runVerificationFlow()
  }, [
    user,
    billingLoaded,
    billingInfo,
    isFreeLicense,
    needsRedirect,
    redirectUrl,
    navigate,
    selectedPlan,
    clearPlan
  ])

  useEffect(() => {
    if (!verificationProcessed) {
      return
    }

    const settingsTab = searchParams.get('settings')
    if (settingsTab) {
      const settingsParams = new URLSearchParams()
      settingsParams.set('tab', settingsTab)

      const conn = searchParams.get('conn')
      if (conn) {
        settingsParams.set('conn', conn)
        settingsParams.set('view', PageView.INDEX_MODAL)
      }

      // We'll handle settings navigation after workspace context is loaded
      // For now, just mark settings as processed and let the main routing handle it
      setSettingsProcessed(true)
      return
    }

    setSettingsProcessed(true)
  }, [verificationProcessed, searchParams])

  // Workspace fetching is now handled by WorkspaceContext

  // Handle workspace navigation - only for root path
  useEffect(() => {
    if (!verificationProcessed || !settingsProcessed) {
      return
    }

    // Wait for workspace loading to complete
    if (workspaceLoading) {
      return
    }

    // If no workspace is available after loading is complete, show error
    if (!currentWorkspace) {
      return
    }

    // Check if we need to navigate to settings
    const settingsTab = searchParams.get('settings')
    if (settingsTab) {
      const settingsParams = new URLSearchParams()
      settingsParams.set('tab', settingsTab)

      const conn = searchParams.get('conn')
      if (conn) {
        settingsParams.set('conn', conn)
        settingsParams.set('view', PageView.INDEX_MODAL)
      }

      const settingsUrl = `/w/${currentWorkspace.uuid}/settings?${settingsParams.toString()}`
      navigate(settingsUrl, { replace: true })
      return
    }

    // Navigate to current workspace - preserve query parameters (like s3_tar_file_path)
    const queryString = searchParams.toString()
    const workspaceUrl = `/w/${currentWorkspace.uuid}${queryString ? `?${queryString}` : ''}`
    navigate(workspaceUrl, { replace: true })
  }, [
    verificationProcessed,
    settingsProcessed,
    workspaceLoading,
    navigate,
    currentWorkspace,
    searchParams
  ])

  // Show loading or error state while routing is being processed
  if (!verificationProcessed || !settingsProcessed || workspaceLoading || lockStatusLoading) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4 mx-auto'></div>
          <p className='text-gray-600'>
            {!verificationProcessed
              ? 'Verifying authentication...'
              : !settingsProcessed
                ? 'Processing settings...'
                : lockStatusLoading
                  ? 'Checking account status...'
                  : 'Loading workspaces...'}
          </p>
        </div>
      </div>
    )
  }

  // If account is locked due to credit debt, show the locked view
  if (isAccountLocked && lockStatus) {
    return (
      <AccountLockedView
        creditDebt={lockStatus.credit_debt}
        minimumCreditsToUnlock={lockStatus.minimum_credits_to_unlock}
        lockReason={lockStatus.lock_reason}
      />
    )
  }

  // If no workspace available, show the NoWorkspaceView component
  if (!currentWorkspace) {
    return <NoWorkspaceView />
  }

  // Should never reach here as navigation effect should trigger
  return (
    <div className='flex items-center justify-center h-screen'>
      <div className='text-center'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4 mx-auto'></div>
        <p className='text-gray-600'>Redirecting to workspace...</p>
      </div>
    </div>
  )
}

function App(): JSX.Element {
  const { loaded: userLoaded } = useUserContext()

  // Wait for user to load
  if (!userLoaded) {
    return <LoadingSpinner />
  }

  return <AppRoutes />
}

export function AppRoutes(): JSX.Element {
  const theme = useSelector((state: RootState) => state.settings.theme)
  const { isAuthenticated, loaded } = useUserContext()
  // Subscribe once for the whole app — toggles a sticky toast when the agent
  // is unreachable. Replaces the old `AppListenersProvider` wrapper, which
  // existed only to call this hook.
  useAgentUnavailableToast()

  useEffect(() => {
    const root = document.documentElement
    const darkThemeMq = window.matchMedia('(prefers-color-scheme: dark)')

    // Suppress every component's `transition-colors`/`transition-all` for the
    // single frame the theme classes flip, so the ~75-variable swap applies
    // instantly instead of staggering into a disjointed fade. Removed on the
    // next frame so normal hover/focus transitions resume. See global.css.
    let rafId = 0
    const swapInstantly = (mutate: () => void): void => {
      root.classList.add('theme-instant')
      mutate()
      // Double rAF: the first lands after the swap's style/layout, the second
      // begins a fresh frame — removing the guard here guarantees no element
      // ever sees a color change while transitions are enabled.
      rafId = window.requestAnimationFrame(() => {
        rafId = window.requestAnimationFrame(() => root.classList.remove('theme-instant'))
      })
    }

    root.dataset.platform = bridge.isInPlugin() ? 'plugin' : 'web'

    swapInstantly(() => {
      root.classList.remove('light', 'dark')

      // Plugin webviews often inherit host dark-mode preference, while many
      // plugin surfaces intentionally use light backgrounds. Force light theme
      // in plugin to avoid global gray/muted text mismatches across components.
      if (bridge.isInPlugin()) {
        root.classList.add('light')
        document.body.style.color = 'rgba(0, 0, 0, 0.88)'
        return
      }

      document.body.style.removeProperty('color')

      if (theme === 'system') {
        root.classList.add(darkThemeMq.matches ? 'dark' : 'light')
      } else {
        root.classList.add(theme)
      }
    })

    const listener = (e: MediaQueryListEvent) => {
      if (theme === 'system') {
        swapInstantly(() => {
          root.classList.toggle('dark', e.matches)
          root.classList.toggle('light', !e.matches)
        })
      }
    }

    // Plugin forces light regardless of theme, so never let an OS preference
    // change flip it (the early-return inside swapInstantly only exits the
    // lambda, not the effect — registration must be guarded here).
    if (!bridge.isInPlugin()) {
      darkThemeMq.addEventListener('change', listener)
    }
    return () => {
      darkThemeMq.removeEventListener('change', listener)
      window.cancelAnimationFrame(rafId)
      root.classList.remove('theme-instant')
    }
  }, [theme])

  // Show loading while user data loads
  if (!loaded) {
    return <LoadingSpinner />
  }

  return (
    <AppRouter>
      <PluginVersionGate>
        <PluginWorkspaceProviderGuard>
          <AuthenticatedAppShell isAuthenticated={isAuthenticated}>
            <Routes>
              {/* Root route controller */}
              <Route
                path='/'
                element={
                  isSuperAdminSubdomain() ? (
                    <ProtectedRoute>
                      <SuperAdminSubdomainRootRouter />
                    </ProtectedRoute>
                  ) : isAuthenticated ? (
                    <AuthenticatedRootController />
                  ) : (
                    <UnauthenticatedRootController />
                  )
                }
              />

              {/* Unauthenticated routes */}
              <Route
                path='/login'
                element={
                  <UnauthenticatedRoute>
                    <SignInPageComponent />
                  </UnauthenticatedRoute>
                }
              />
              <Route
                path='/signin'
                element={
                  <UnauthenticatedRoute>
                    <SignInPageComponent />
                  </UnauthenticatedRoute>
                }
              />
              <Route
                path='/signup'
                element={
                  <UnauthenticatedRoute>
                    <SignUpPageComponent />
                  </UnauthenticatedRoute>
                }
              />
              <Route
                path='/reset-password'
                element={
                  <UnauthenticatedRoute>
                    <ResetPasswordPageComponent />
                  </UnauthenticatedRoute>
                }
              />
              <Route
                path='/sso-setup'
                element={
                  <UnauthenticatedRoute>
                    <SsoSetupPageComponent />
                  </UnauthenticatedRoute>
                }
              />

              {/* Routes accessible during authentication flow */}
              <Route path='/verify-email' element={<VerifyEmailPageComponent />} />
              <Route path='/setup-mfa' element={<SetupMfaPageComponent />} />
              <Route path='/phone-verification' element={<PhoneVerificationPage />} />

              {/* Protected authenticated routes */}

              {/* Workspace-first routing - MUST come before super admin catch-all on subdomain */}
              <Route
                path='/w/:workspaceUuid/*'
                element={
                  <ProtectedRoute>
                    <WorkspaceValidationComponent>
                      <AccountLockGuard>
                        <Routes>
                          <Route
                            index
                            element={
                              <AppLayoutWithSidebar>
                                <WorkspaceHomePage />
                              </AppLayoutWithSidebar>
                            }
                          />
                          <Route
                            path='c/:uuid/*'
                            element={
                              <CurrentConversationContextProvider>
                                <AppLayoutWithSidebar>
                                  <ConversationLayoutWithOptionalTask />
                                </AppLayoutWithSidebar>
                              </CurrentConversationContextProvider>
                            }
                          />
                          <Route
                            path='settings'
                            element={
                              <AppLayoutWithSidebar>
                                <Settings />
                              </AppLayoutWithSidebar>
                            }
                          />
                        </Routes>
                      </AccountLockGuard>
                    </WorkspaceValidationComponent>
                  </ProtectedRoute>
                }
              />

              {/* Super admin subdomain catch-all - placed AFTER /w/* so workspace routes match first */}
              {isSuperAdminSubdomain() && (
                <Route
                  path='/:tab/*'
                  element={
                    <ProtectedRoute>
                      <SuperAdminTabGuard>
                        <SuperAdminAccessGuard>
                          <SuperAdminPageComponent />
                        </SuperAdminAccessGuard>
                      </SuperAdminTabGuard>
                    </ProtectedRoute>
                  }
                />
              )}

              <Route
                path='/super-admin/*'
                element={
                  isPlatformDomain() ? (
                    <PlatformSuperAdminRedirect />
                  ) : (
                    <ProtectedRoute>
                      <SuperAdminAccessGuard>
                        <SuperAdminPageComponent />
                      </SuperAdminAccessGuard>
                    </ProtectedRoute>
                  )
                }
              />
              <Route
                path='/human-annotations'
                element={
                  <ProtectedRoute>
                    <HumanAnnotationsPageComponent />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/onboard'
                element={
                  <ProtectedRoute>
                    <OnboardPageComponent />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/connect'
                element={
                  <ProtectedRoute>
                    <SimplePage title='Connect' content='Connect page will be implemented here' />
                  </ProtectedRoute>
                }
              />
              <Route path='/join-account' element={<JoinAccountPage />} />
              <Route path='/accept-billing-invite' element={<AcceptBillingInvitePage />} />
              <Route path='/checkout-callback' element={<CheckoutCallbackPage />} />

              {/* Public routes */}
              <Route path='/plugin-auth-success' element={<PluginAuthSuccessPageComponent />} />
              <Route path='/terms' element={<TermsPageComponent />} />
              <Route path='/privacy' element={<PrivacyPageComponent />} />
              <Route
                path='/master-license-agreement'
                element={<MasterLicenseAgreementPageComponent />}
              />

              {/* More protected routes */}
              <Route
                path='/test-drive'
                element={
                  <ProtectedRoute>
                    <TestDrivePageComponent />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/dwl-sample-data-generator'
                element={
                  <ProtectedRoute>
                    <DwlSampleDataGeneratorPageComponent />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/input-output-to-dwl'
                element={
                  <ProtectedRoute>
                    <SimplePage
                      title='Input Output to DWL'
                      content='Input output to DWL converter will be implemented here'
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/flow-doc-generator'
                element={
                  <ProtectedRoute>
                    <FlowDocGeneratorPageComponent />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/mt-to-dwl'
                element={
                  <ProtectedRoute>
                    <MtToDwlPageComponent />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </AuthenticatedAppShell>
        </PluginWorkspaceProviderGuard>
      </PluginVersionGate>
    </AppRouter>
  )
}

function AppRouter({ children }: { children: React.ReactNode }) {
  const routerFuture = { v7_startTransition: true, v7_relativeSplatPath: true }

  if (bridge.isInPlugin()) {
    return <MemoryRouter future={routerFuture}>{children}</MemoryRouter>
  }
  return <BrowserRouter future={routerFuture}>{children}</BrowserRouter>
}

function UtilsProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children}
      <FlashMessageHandler />
      <FlashMessageUrlHandler />
    </>
  )
}

function PluginWorkspaceProviderGuard({ children }: { children: React.ReactNode }) {
  if (bridge.isInPlugin()) {
    return <PluginWorkspaceProvider>{children}</PluginWorkspaceProvider>
  }
  return <>{children}</>
}

function DesktopLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const { collapsed, setCollapsed } = useSidebarContext()
  const didSyncDesktopSidebarRef = useRef(false)

  // Desktop currently comes back expanded after a full refresh, so sync the
  // context once on mount to avoid leaving headers in the collapsed state.
  useLayoutEffect(() => {
    if (!didSyncDesktopSidebarRef.current && collapsed) {
      didSyncDesktopSidebarRef.current = true
      setCollapsed(false)
      return
    }

    didSyncDesktopSidebarRef.current = true
  }, [collapsed, setCollapsed])

  return (
    <div className='h-screen w-screen flex flex-col'>
      <div className='flex grow bg-tokens-neutral-bg-grey-1 text-tokens-neutral-text-primary min-h-0'>
        <Container
          id='sidebar|main'
          orientation={Orientation.HORIZONTAL}
          className='w-screen bg-tokens-neutral-bg-grey-1'
          initialSize={271}
          isResizable
          collapsed={collapsed}
          firstChild={<Sidebar handleRoot={() => navigate('/')} fullWidth />}
          firstClassName={cm(
            'overflow-hidden bg-tokens-neutral-bg-white',
            'border-r border-tokens-neutral-border-2'
          )}
          secondClassName='overflow-auto bg-tokens-neutral-bg-grey-1'
          secondChild={children}
        />
      </div>
    </div>
  )
}

export function AppLayoutWithSidebar({ children }: { children: React.ReactNode }) {
  const isPlugin = bridge.isInPlugin()

  if (isPlugin) {
    return (
      <SidebarProvider>
        <PluginSinglePanelLayout>{children}</PluginSinglePanelLayout>
      </SidebarProvider>
    )
  }

  return (
    <SidebarProvider>
      <DesktopLayout>{children}</DesktopLayout>
    </SidebarProvider>
  )
}

/**
 * Plugin layout: content is always visible, sidebar slides in as an overlay
 * drawer from the left with a dimmed backdrop.
 */
function PluginSinglePanelLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { collapsed, setCollapsed } = useSidebarContext()

  const showingSidebar = !collapsed
  const closeSidebar = useCallback(() => setCollapsed(true), [setCollapsed])

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const prevPathRef = useRef(location.pathname)
  useEffect(() => {
    const prev = prevPathRef.current
    prevPathRef.current = location.pathname
    if (prev !== location.pathname) {
      setCollapsed(true)
    }
  }, [location.pathname, setCollapsed])

  return (
    <div className='h-screen w-full flex flex-col relative overflow-hidden bg-light-background dark:bg-dark-background text-light-on-background dark:text-dark-on-background'>
      <PluginContentHeader onToggleSidebar={() => setCollapsed(false)} />
      <div className='flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden'>
        {children}
      </div>

      <div
        className={cm(
          'absolute inset-0 z-40 transition-opacity duration-200',
          showingSidebar ? 'bg-black/30' : 'opacity-0 pointer-events-none'
        )}
        onClick={closeSidebar}
      />

      <div
        className={cm(
          'absolute inset-y-0 left-0 z-50 w-[85vw] max-w-[320px] shadow-xl',
          'transition-transform duration-200 ease-out',
          showingSidebar ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <Sidebar
          handleRoot={() => {
            navigate('/')
            closeSidebar()
          }}
          fullWidth
        />
      </div>
    </div>
  )
}

function PluginContentHeader({ onToggleSidebar }: { onToggleSidebar: () => void }) {
  const location = useLocation()
  const navigate = useNavigate()
  const isSettingsPage = location.pathname.includes('/settings')
  const pageLabel = isSettingsPage ? 'Settings' : ''

  const handleNewChat = () => {
    const workspaceMatch = location.pathname.match(/\/w\/([^/]+)/)
    if (workspaceMatch) {
      navigate(`/w/${workspaceMatch[1]}`)
    }
  }

  return (
    <div className='flex items-center h-10 px-3 shrink-0 border-b border-[#eaeaea] bg-white gap-2'>
      <button
        onClick={onToggleSidebar}
        className='p-1.5 rounded-md hover:bg-gray-100 transition-colors shrink-0'
        aria-label='Show sidebar'
      >
        <svg
          width='18'
          height='18'
          viewBox='0 0 20 20'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M16.4736 0.926758C17.9094 0.926758 19.0732 2.09065 19.0732 3.52637V16.4736C19.0732 17.9094 17.9094 19.0732 16.4736 19.0732H3.52637C2.09074 19.0731 0.926758 17.9093 0.926758 16.4736V3.52637C0.926798 2.09074 2.09077 0.926904 3.52637 0.926758H16.4736ZM7.97559 17.5732H16.4736C17.081 17.5732 17.5732 17.081 17.5732 16.4736V3.52637C17.5732 2.91908 17.0809 2.42676 16.4736 2.42676H7.97559V17.5732ZM3.52637 2.42676C2.9192 2.4269 2.4268 2.91917 2.42676 3.52637V16.4736C2.42676 17.0809 2.91917 17.5731 3.52637 17.5732H6.47559V2.42676H3.52637Z'
            fill='black'
            fillOpacity='0.45'
          />
        </svg>
      </button>

      {pageLabel && (
        <Typography
          fontFamily='geist'
          fontSize='bodySm'
          fontWeight='medium'
          className='text-[rgba(0,0,0,0.65)] truncate'
        >
          {pageLabel}
        </Typography>
      )}

      <div className='ml-auto'>
        <button
          onClick={handleNewChat}
          className='p-1.5 rounded-md hover:bg-gray-100 transition-colors'
          aria-label='New chat'
        >
          <svg
            width='18'
            height='18'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g clipPath='url(#clip_new_chat)'>
              <path
                d='M9.1665 3.33325H3.33317C2.89114 3.33325 2.46722 3.50885 2.15466 3.82141C1.8421 4.13397 1.6665 4.55789 1.6665 4.99992V16.6666C1.6665 17.1086 1.8421 17.5325 2.15466 17.8451C2.46722 18.1577 2.89114 18.3333 3.33317 18.3333H14.9998C15.4419 18.3333 15.8658 18.1577 16.1783 17.8451C16.4909 17.5325 16.6665 17.1086 16.6665 16.6666V10.8333'
                stroke='black'
                strokeOpacity='0.45'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M15.4165 2.08344C15.748 1.75192 16.1977 1.56567 16.6665 1.56567C17.1353 1.56567 17.585 1.75192 17.9165 2.08344C18.248 2.41496 18.4343 2.8646 18.4343 3.33344C18.4343 3.80228 18.248 4.25192 17.9165 4.58344L9.99984 12.5001L6.6665 13.3334L7.49984 10.0001L15.4165 2.08344Z'
                stroke='black'
                strokeOpacity='0.45'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </g>
            <defs>
              <clipPath id='clip_new_chat'>
                <rect width='20' height='20' fill='white' />
              </clipPath>
            </defs>
          </svg>
        </button>
      </div>
    </div>
  )
}

function PluginWorkspaceIndicator() {
  const { projects, selectedWorkspace, selectWorkspace, isLoading } = usePluginWorkspace()
  const [open, setOpen] = useState(false)

  if (isLoading) {
    return <span className='text-xs text-default-400'>Loading...</span>
  }

  if (projects.length === 0) {
    return (
      <div className='flex items-center gap-1.5'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 20 20'
          fill='currentColor'
          className='w-3.5 h-3.5 text-warning'
        >
          <path d='M3.75 3A1.75 1.75 0 002 4.75v3.26a3.235 3.235 0 011.75-.51h12.5c.644 0 1.245.188 1.75.51V6.75A1.75 1.75 0 0016.25 5h-4.836a.25.25 0 01-.177-.073L9.823 3.513A1.75 1.75 0 008.586 3H3.75zM3.75 9A1.75 1.75 0 002 10.75v4.5c0 .966.784 1.75 1.75 1.75h12.5A1.75 1.75 0 0018 15.25v-4.5A1.75 1.75 0 0016.25 9H3.75z' />
        </svg>
        <span className='text-xs font-medium text-warning'>No Workspace</span>
      </div>
    )
  }

  return (
    <div className='relative'>
      <button
        type='button'
        onClick={() => setOpen(v => !v)}
        className='flex items-center gap-1.5 rounded-md px-1.5 py-1 hover:bg-gray-100 transition-colors'
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 20 20'
          fill='currentColor'
          className='w-3.5 h-3.5 text-[rgba(0,0,0,0.35)]'
        >
          <path d='M3.75 3A1.75 1.75 0 002 4.75v3.26a3.235 3.235 0 011.75-.51h12.5c.644 0 1.245.188 1.75.51V6.75A1.75 1.75 0 0016.25 5h-4.836a.25.25 0 01-.177-.073L9.823 3.513A1.75 1.75 0 008.586 3H3.75zM3.75 9A1.75 1.75 0 002 10.75v4.5c0 .966.784 1.75 1.75 1.75h12.5A1.75 1.75 0 0018 15.25v-4.5A1.75 1.75 0 0016.25 9H3.75z' />
        </svg>
        <span className='text-xs font-medium text-[rgba(0,0,0,0.65)] truncate'>
          {selectedWorkspace?.name || 'Select workspace'}
        </span>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 16 16'
          fill='currentColor'
          className='w-3 h-3 text-[rgba(0,0,0,0.25)]'
        >
          <path
            fillRule='evenodd'
            d='M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z'
            clipRule='evenodd'
          />
        </svg>
      </button>
      {open && (
        <>
          <div className='fixed inset-0 z-[99]' onClick={() => setOpen(false)} />
          <div className='absolute top-full left-0 mt-1 z-[100] min-w-[180px] rounded-lg border border-[#eaeaea] bg-white shadow-lg py-1'>
            {projects.map(p => (
              <button
                key={p.path}
                type='button'
                onClick={() => {
                  selectWorkspace(p.path)
                  setOpen(false)
                }}
                className={cm(
                  'w-full text-left px-3 py-2 text-xs',
                  p.path === selectedWorkspace?.path
                    ? 'bg-gray-100 font-medium text-default-800'
                    : 'text-default-600 hover:bg-gray-50'
                )}
              >
                {p.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export function Settings(): JSX.Element {
  return (
    <div className='bg-tokens-neutral-bg-grey-1 h-full overflow-y-auto [scrollbar-gutter:stable]'>
      <TopAlertBanner />
      <SettingsPage />
    </div>
  )
}

export function ConversationLayoutWithOptionalTask(): JSX.Element {
  const [searchParams] = useSearchParams()
  const { openPlan } = useModalContext()
  const {
    conversation,
    isLoading,
    showLoadingIndicator,
    dataHidden,
    creditsUsed,
    creditsLimit,
    uuid: contextUuid
  } = useCurrentConversation()
  const taskId = searchParams.get('task_id')

  // Check if we're in a transitional state where the URL has changed but data hasn't loaded yet.
  // This happens during navigation (e.g., after deleting a conversation).
  // Show loading if:
  // 1. We have a URL uuid but no conversation loaded yet
  // 2. We have a URL uuid that doesn't match the loaded conversation
  // Note: Exclude dataHidden case - when data is hidden due to credit limit,
  // conversation is null but we're not transitioning, we just can't show the data
  const isTransitioning =
    contextUuid && !dataHidden && (!conversation || conversation.uuid !== contextUuid)

  if (isLoading || isTransitioning) {
    // Only show loading indicator after 500ms delay
    if (showLoadingIndicator) {
      return <LoadingState message={taskId ? 'Loading task...' : 'Loading...'} className='h-full' />
    }
    // During initial load, show empty container to avoid flash
    return <div className='h-full' />
  }

  // Credit limit exceeded - show upgrade/purchase message (applies to all users in prepay model)
  if (dataHidden) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='flex flex-col items-center justify-center p-8 text-center max-w-md'>
          <div className='text-on-background dark:text-light10 mb-4'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
              strokeWidth={1.5}
              stroke='currentColor'
              className='w-16 h-16 mx-auto mb-4 text-tokens-neutral-fill-fill-3'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z'
              />
            </svg>
          </div>
          <h2 className='text-xl font-semibold text-on-background dark:text-light10 mb-2'>
            Credit Limit Reached
          </h2>
          <p className='text-sm text-tokens-neutral-text-text-1 mb-4'>
            You&apos;ve used {creditsUsed} of {creditsLimit} credits.
            <br />
            Purchase additional credits or upgrade your plan to continue viewing this conversation.
          </p>
          <button
            onClick={openPlan}
            className='px-6 py-3 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors'
          >
            Purchase Credits
          </button>
        </div>
      </div>
    )
  }

  if (!conversation) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-center max-w-md p-4'>
          <div className='text-gray-400 mb-4'>
            <svg
              className='w-12 h-12 mx-auto'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-4.906-1.444l-3.077 1.028a2 2 0 01-2.444-2.444l1.028-3.077C3.222 12.964 3 11.014 3 9c0-4.418 3.582-8 8-8s8 3.582 8 8z'
              />
            </svg>
          </div>
          <h3 className='text-lg font-semibold text-gray-900 mb-2'>Conversation not found</h3>
          <p className='text-gray-600'>
            This conversation may have been deleted or you may not have access to it.
          </p>
        </div>
      </div>
    )
  }

  if (taskId) {
    return (
      <TaskProvider>
        <ConversationLayout />
      </TaskProvider>
    )
  }

  return <ConversationLayout />
}

export function ConversationLayout(): JSX.Element {
  const {
    conversation,
    isLoading,
    showLoadingIndicator,
    uuid: contextUuid,
    dataHidden
  } = useCurrentConversation()
  const [searchParams] = useSearchParams()
  const taskId = searchParams.get('task_id')

  // Check for transitional state (should rarely hit this as parent checks, but be defensive)
  // Note: Exclude dataHidden case - when data is hidden, conversation is null but we're not transitioning
  const isTransitioning =
    contextUuid && !dataHidden && (!conversation || conversation.uuid !== contextUuid)
  if (isLoading || isTransitioning) {
    // Only show loading indicator after 500ms delay
    if (showLoadingIndicator) {
      return <LoadingState message={taskId ? 'Loading task...' : 'Loading...'} className='h-full' />
    }
    return <div className='h-full' />
  }

  return (
    <Routes>
      <Route path='/integration-test/results' element={<TestAgentResultScreen />} />
      <Route path='/*' element={<ConversationPage key={conversation?.uuid} />} />
    </Routes>
  )
}

function ConversationPage() {
  const isPlugin = bridge.isInPlugin()

  if (isPlugin) {
    return (
      <div className='grow min-h-0 relative bg-tokens-neutral-bg-grey-1 h-full'>
        <ConversationView />
      </div>
    )
  }

  return (
    <div className='grow min-h-0 relative bg-tokens-neutral-bg-grey-1 h-full'>
      <Container
        id='chat|workspace'
        orientation={Orientation.HORIZONTAL}
        className='grow h-full min-h-0 min-w-0'
        initialSize={1000}
        isResizable={true}
        firstChild={
          <div className='grow min-h-0 relative bg-tokens-neutral-bg-grey-1 h-full'>
            <ConversationView />
          </div>
        }
        resizeBarClassName='justify-end border-tokens-neutral-border-2'
      />
    </div>
  )
}

export function ConversationView() {
  const {
    conversation,
    refreshCurrentConversation,
    isLoading: isConvLoading,
    showLoadingIndicator,
    uuid: contextUuid,
    tasks: conversationTasks
  } = useCurrentConversation()
  const { refetch: refetchConversations } = useConversationsContext()
  const isTaskLoading = isConvLoading
  const { setActiveTab } = useSidebarContext()

  // Check for transitional state (URL changed but conversation not loaded yet)
  const isTransitioning = contextUuid && (!conversation || conversation.uuid !== contextUuid)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const taskId = searchParams.get('task_id')
  const { uuid, workspaceUuid } = useParams<{ uuid?: string; workspaceUuid?: string }>()
  const conversationMode = conversation?.conversation_metadata?.mode
  const commandType = commandTypeFromJSON(conversationMode)
  const shouldUseChatDisplay =
    commandType === CommandType.MEGA_QA_AGENT_V1 || isChatCommand(commandType as UsedCommandType)
  const traceWithUserInfo = useTracer()

  useEffect(() => {
    const handleClickOutside = () => store.dispatch(hideExamplePopover())
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  // Centralized routing logic for /c/uuid
  useEffect(() => {
    if (!conversation || isTaskLoading) {
      // Skip if no conversation, no UUID, already has task_id, or still loading tasks
      return
    }

    // IMPORTANT: Check if conversation data matches the current URL
    // This prevents race conditions where stale conversation data triggers incorrect redirects
    // when navigating between conversations via sidebar
    if (conversation.uuid !== uuid) {
      // Conversation data is stale (doesn't match URL) - skip redirect logic
      return
    }

    // Check conversation mode
    const conversationMode = conversation.conversation_metadata?.mode
    const commandType = commandTypeFromJSON(conversationMode)

    if (
      isChatCommand(commandType as UsedCommandType) ||
      commandType === CommandType.MEGA_QA_AGENT_V1
    ) {
      // MEGA_QA_AGENT_V1 mode, keep it in /c/uuid
      return
    }

    if (conversationTasks && conversationTasks.length > 0) {
      if (conversationTasks.map(task => task.task_id).includes(Number(taskId))) return
      const conversationUrl = `/w/${workspaceUuid}/c/${conversation.uuid}`
      navigate(`${conversationUrl}?task_id=${conversationTasks[0].task_id}`, { replace: true })
    } else if (!isTaskLoading) {
      // Only navigate away from task_id if we've finished loading tasks and confirmed there are none
      // Don't remove task_id while tasks are still loading
      if (!taskId) return
      const conversationUrl = workspaceUuid
        ? `/w/${workspaceUuid}/c/${conversation.uuid}`
        : `/c/${conversation.uuid}`
      navigate(conversationUrl, { replace: true })
    }
  }, [conversation, conversationTasks, isTaskLoading, taskId, workspaceUuid, navigate, uuid])

  // Task creation is now handled locally in TaskForm.tsx
  // No global event handler needed

  const onSubmitFixedPromptTask = useCallback(
    async ({
      command,
      withExamplePrefilled = false,
      repositories,
      s3UploadPaths
    }: FixedPromptTaskProps) => {
      const metadata = Metadata.fromPartial({})
      if (s3UploadPaths) {
        const codeContext = s3UploadPaths.map(path =>
          CodeContext.fromPartial({
            connType: 'S3',
            s3TarFilePath: path
          })
        )
        if (s3UploadPaths.length > 1) {
          metadata.multiRepoCodeContext = codeContext
        } else {
          metadata.codeContext = codeContext[0]
        }
      }
      if (repositories) {
        const codeContext = repositories.map(r => {
          const [orgName, repoName] = parseRepositoryName(r.repository)
          return CodeContext.fromPartial({
            connType: r.conn_type,
            orgName,
            repoName,
            branchName: r.branch
          })
        })
        if (repositories.length > 1) {
          metadata.multiRepoCodeContext = codeContext
        } else {
          metadata.codeContext = codeContext[0]
        }
      }
      if (withExamplePrefilled) {
        try {
          const { file } = await loadExampleProjectAsFile()
          if (!workspaceUuid) {
            BetterToast.error('Workspace UUID is required')
            return
          }
          s3UploadPaths = [await loadExampleProjectAsS3Path(file, workspaceUuid)]
        } catch (error) {
          console.error('Failed to load example project:', error)
          BetterToast.error('Failed to load example project')
          return
        }
      }

      let isSuccess = false
      if (FixedPromptCommands.includes(command.id)) {
        switch (command.id) {
          case CommandType.GENERATE_FULL_REPO_TECH_MANIFEST_DOCUMENTATION:
            isSuccess = await createTask({
              request: AgentRequest.fromPartial({
                command: {
                  commandType: command.id,
                  commandString: 'generate the documentation of the entire repository'
                },
                metadata
              })
            })
            break
          case CommandType.GENERATE_MULTI_REPO_FLOW_DIAGRAM:
            isSuccess = await createTask({
              request: AgentRequest.fromPartial({
                command: {
                  commandType: command.id,
                  commandString: 'give me the diagram showing dependencies across all respositories'
                },
                metadata
              })
            })
            break
        }
      }
      if (isSuccess) {
        traceWithUserInfo({
          event: 'try_own_example',
          labels: {
            command: command.id.toString()
          }
        })
        // Backend will update conversation metadata and emit socket event
        // Socket listeners will automatically update the UI
        Promise.all([refreshCurrentConversation(), refetchConversations()])
        store.dispatch(hideNonFormPopover())
      }
    },
    [traceWithUserInfo, refreshCurrentConversation, refetchConversations, workspaceUuid]
  )

  useEffect(() => {
    globalEventEmitter.globalOn(
      AppEvents.SubmitFixedPromptTask,
      onSubmitFixedPromptTask as (...args: unknown[]) => void
    )
  }, [onSubmitFixedPromptTask])
  if (!conversation || isConvLoading || isTransitioning) {
    // Only show spinner after 500ms delay
    if (showLoadingIndicator) return <LoadingSpinner />
    return <div className='h-full' />
  }

  // If there's a task_id parameter, render the workspace with task display
  if (taskId && uuid) {
    const handleCloseTask = () => {
      if (shouldUseChatDisplay) {
        const newParams = new URLSearchParams(searchParams)
        newParams.delete('task_id')
        setSearchParams(newParams, { replace: true })
      } else {
        navigate(`/`, { replace: true })
      }
    }

    return (
      <>
        <TaskDisplay onClose={handleCloseTask} />
        <CommonModal
          id={ModalId.TaskStatusDetails}
          Component={TaskStatusDetailModal as unknown as FC<Record<string, unknown>>}
        />
        <CommonModal
          id={ModalId.RefactorFileViewer}
          Component={RefactorFileViewerSlide as unknown as FC<Record<string, unknown>>}
        />
        <CommonModal
          id={ModalId.ImageViewer}
          Component={ImageViewerModal as unknown as FC<Record<string, unknown>>}
        />
      </>
    )
  }

  return (
    <div className='flex flex-col h-full relative'>
      {/* Bound this wrapper to the available height WITHOUT scrolling so that
          ChatDisplay's own overflow-auto container is the real scroller. When
          this wrapper scrolled (overflow-y-auto), the h-screen ChatDisplay
          overflowed here and ChatDisplay's scroll machinery — older-message
          pagination, the scroll-to-bottom pin, and "user scrolled up" tracking
          — never fired, because its internal container never moved. */}
      <div className='flex-1 min-h-0 overflow-hidden'>
        <ChatDisplayController onConversationCreated={() => setActiveTab('chats')} />
      </div>
    </div>
  )
}

interface UserSetupHandlerProps {
  children: React.ReactNode
}

export function UserSetupHandler({ children }: UserSetupHandlerProps) {
  const user = useUser()
  const { currentWorkspace } = useWorkspaceContext()

  useEffect(() => {
    if (!user?.email) return
    const initializeUser = async () => {
      try {
        // Initialize user directory handles - verification is now handled at root level
        await initializeUserAndHandles(user.email)

        // Start the cron scheduler only once after successful initialization
        await cronScheduler.start()
      } catch (error) {
        console.error('Failed to initialize directory handles:', error)

        // Even if user handle initialization fails, we should still start the scheduler
        // This ensures the scheduler is running for potential future operations
        try {
          await cronScheduler.start()
        } catch (schedulerError) {
          console.error('Failed to start cronScheduler:', schedulerError)
        }
      }
    }

    initializeUser()

    // Cleanup function to stop scheduler when component unmounts or dependencies change
    return () => {
      cronScheduler.stop()
    }
  }, [user])

  // Update workspace UUID in cronScheduler when workspace changes
  useEffect(() => {
    if (currentWorkspace?.uuid) {
      cronScheduler.setWorkspaceUuid(currentWorkspace.uuid)
      console.info(`🔄 Workspace context updated in cronScheduler: ${currentWorkspace.uuid}`)
    }
  }, [currentWorkspace])

  return <>{children}</>
}

/**
 * Manages the user-level WebSocket connection at the auth level.
 *
 * Lives above all route-level providers so the connection persists across
 * route transitions (e.g. `/` → `/w/{uuid}`). Without this, navigating
 * between routes that each mount their own WorkspaceProvider would cause
 * unnecessary disconnect/reconnect cycles.
 */
function UserSocketManager({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useUserContext()
  const { billingInfo } = useBillingContext()
  const billingId = billingInfo?.id

  useEffect(() => {
    if (!isAuthenticated || !billingId) {
      return
    }

    if (!UserSocket.isConnected()) {
      console.log('[UserSocketManager] Connecting UserSocket (primary WebSocket)...')
      UserSocket.connect()
    }

    return () => {
      console.log('[UserSocketManager] Disconnecting UserSocket...')
      UserSocket.disconnect()
    }
  }, [isAuthenticated, billingId])

  return <>{children}</>
}

export function AuthContextProvider({ children }: { children: React.ReactNode }) {
  return (
    <UserProvider>
      <BillingAccountsProvider>
        <BillingProvider>
          <ImpersonationProvider>
            <UserSocketManager>{children}</UserSocketManager>
          </ImpersonationProvider>
        </BillingProvider>
      </BillingAccountsProvider>
    </UserProvider>
  )
}

export const Page: FC = () => {
  return (
    <PlatformProvider>
      <PersistQueryClientProvider persistOptions={{ persister }} client={queryClient}>
        <Provider store={store}>
          <NextUIProvider>
            <AppConfigProvider>
              <AuthContextProvider>
                <ImpersonationBanner />
                <Toaster toastOptions={{ duration: 6000 }} containerStyle={{ zIndex: 99999 }} />
                <Errors />
                <App />
              </AuthContextProvider>
            </AppConfigProvider>
          </NextUIProvider>
        </Provider>
      </PersistQueryClientProvider>
    </PlatformProvider>
  )
}

createRoot(document.getElementById('root')!).render(<Page />)
